/**
 * Merge a freshly-fetched server list of in-production items into the current
 * client state while respecting rows the local user is actively editing.
 *
 * Server is authoritative for membership, ordering, and server-owned fields,
 * EXCEPT for ids passed in `options.activeIds`. For active ids, the local row
 * is preserved verbatim and any divergent server payload is returned in the
 * `pending` map so the UI can surface a non-blocking "updated remotely" hint.
 * When the user finishes (modal closes, menu collapses, etc.), the caller can
 * trigger an immediate re-sync to flush pending changes into the list.
 *
 * @param {Array} prev      current client state
 * @param {Array} next      server response items
 * @param {{ activeIds?: Set<string> }} options
 * @returns {{ items: Array, pending: Map<string, Object|null> }}
 *   - items: the list React should render (server order; active rows held).
 *   - pending: id → remote item that was withheld; `null` value means the
 *     server has dropped the row from the current view but we are holding it
 *     until the user releases it.
 */
export function mergeInProdItems(prev, next, options = {}) {
    const activeIds = options.activeIds instanceof Set ? options.activeIds : new Set();
    const prevList = Array.isArray(prev) ? prev : [];

    if (!Array.isArray(next)) {
        return { items: prevList, pending: new Map() };
    }

    const prevById = new Map();
    prevList.forEach((it, idx) => {
        if (it && it._id != null) prevById.set(String(it._id), { item: it, idx });
    });

    const seenInNext = new Set();
    const pending = new Map();
    const merged = [];
    let changed = prevList.length !== next.length;

    for (let i = 0; i < next.length; i++) {
        const serverItem = next[i];
        const id = serverItem && serverItem._id != null ? String(serverItem._id) : null;
        if (id != null) seenInNext.add(id);
        const localEntry = id != null ? prevById.get(id) : null;
        const local = localEntry ? localEntry.item : null;

        if (id != null && activeIds.has(id)) {
            // Active row: keep the local copy intact. Surface divergent remote
            // payload via `pending` for a non-blocking hint.
            if (local) {
                if (!shallowEqualOwnedFields(local, serverItem)) {
                    pending.set(id, serverItem);
                }
                merged.push(local);
                if (!changed && prevList[i] !== local) changed = true;
                continue;
            }
            // Active id but no local row (rare race): take server as-is.
            merged.push(serverItem);
            changed = true;
            continue;
        }

        if (!local) {
            merged.push(serverItem);
            changed = true;
            continue;
        }

        const result = { ...local, ...serverItem };
        if (!changed) {
            if (prevList[i] !== local) changed = true;
            else if (!shallowEqual(local, result)) changed = true;
        }
        merged.push(result);
    }

    // Active items no longer present in the server view: hold them in place so
    // the user's open modal/menu still references something concrete. Record
    // pending removal (value=null) so the UI can hint at it.
    for (const id of activeIds) {
        if (seenInNext.has(id)) continue;
        const localEntry = prevById.get(id);
        if (!localEntry) continue;
        pending.set(id, null);
        const insertAt = Math.min(localEntry.idx, merged.length);
        merged.splice(insertAt, 0, localEntry.item);
        changed = true;
    }

    return { items: changed ? merged : prevList, pending };
}

/**
 * Shallow-compare two pending maps (id → remote item OR null). Used by callers
 * to decide whether to re-render the "updated remotely" hint.
 */
export function pendingMapsEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.size !== b.size) return false;
    for (const [k, v] of a) {
        if (!b.has(k)) return false;
        const other = b.get(k);
        if (v === null || other === null) {
            if (v !== other) return false;
            continue;
        }
        if (!shallowEqualOwnedFields(v, other)) return false;
    }
    return true;
}

const OWNED_FIELDS = [
    'status',
    'productionType',
    'assignedAdminUserId',
    'productionOwnerUserId',
    'clientFeedbackStatus',
    'clientFeedbackComment',
    'publicationUrl',
    'isArchived',
    'articleCountSnapshot',
    'sortOrder',
    'bucketId',
];

function shallowEqualOwnedFields(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    for (const f of OWNED_FIELDS) {
        if (a[f] !== b[f]) return false;
    }
    return true;
}

function shallowEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
        if (a[k] !== b[k]) return false;
    }
    return true;
}
